import type { ScorecardRow } from '@troia-v3/types';

import { formatBRL, formatNumber, formatPct } from '@/shared/components/ui';

/**
 * Colunas da tabela "Time completo". `#` e "Vendedor" são fixas e não entram
 * aqui. A ORDEM do array é a ordem das colunas — o usuário escolhe quais
 * aparecem, não onde; com o padrão, a tabela sai idêntica à de antes.
 */
export const SCORECARD_COLUMN_IDS = [
  'leads',
  'activities',
  'revenue',
  'deals',
  'conversionRate',
  'avgTicket',
  'capturedLeads',
  'capturedWon',
  'capturedConversionRate',
  'capturedRevenue',
  'calls',
  'emails',
  'messages',
  'meetings',
  'goal',
] as const;

export type ScorecardColumnId = (typeof SCORECARD_COLUMN_IDS)[number];

export const DEFAULT_SCORECARD_COLUMNS: ScorecardColumnId[] = [
  'leads',
  'activities',
  'revenue',
  'deals',
  'conversionRate',
  'avgTicket',
  'calls',
  'emails',
  'messages',
  'meetings',
  'goal',
];

export interface ScorecardColumnDef {
  id: ScorecardColumnId;
  /** Cabeçalho curto da tabela. */
  header: string;
  /** Nome completo — no menu de colunas e no card do celular. */
  label: string;
  /** Texto do valor. `goal` é desenhado à parte (barra), por isso é opcional. */
  format?: (row: ScorecardRow) => string;
  bold?: boolean;
}

/**
 * `?? 0` nos campos de captura: no deploy, a tela nova pode receber por
 * instantes a resposta do servidor antigo, sem eles.
 */
export const SCORECARD_COLUMNS: Record<ScorecardColumnId, ScorecardColumnDef> = {
  leads: { id: 'leads', header: 'Leads', label: 'Leads', format: (r) => formatNumber(r.leads) },
  activities: {
    id: 'activities',
    header: 'Ativ.',
    label: 'Atividades',
    format: (r) => formatNumber(r.activities.total),
  },
  revenue: {
    id: 'revenue',
    header: 'Receita',
    label: 'Receita',
    format: (r) => formatBRL(r.revenue),
    bold: true,
  },
  deals: { id: 'deals', header: 'Neg.', label: 'Negócios ganhos', format: (r) => formatNumber(r.deals) },
  conversionRate: {
    id: 'conversionRate',
    header: 'Conv.',
    label: 'Conversão geral',
    format: (r) => formatPct(r.conversionRate),
  },
  avgTicket: { id: 'avgTicket', header: 'Ticket', label: 'Ticket médio', format: (r) => formatBRL(r.avgTicket) },
  capturedLeads: {
    id: 'capturedLeads',
    header: 'Captados',
    label: 'Leads captados (formulário de captura)',
    format: (r) => formatNumber(r.capturedLeads ?? 0),
  },
  capturedWon: {
    id: 'capturedWon',
    header: 'Vend. capt.',
    label: 'Vendas via captura',
    format: (r) => formatNumber(r.capturedWon ?? 0),
  },
  capturedConversionRate: {
    id: 'capturedConversionRate',
    header: 'Conv. capt.',
    label: 'Conversão via captura',
    format: (r) => formatPct(r.capturedConversionRate ?? 0),
  },
  capturedRevenue: {
    id: 'capturedRevenue',
    header: 'Rec. capt.',
    label: 'Receita via captura',
    format: (r) => formatBRL(r.capturedRevenue ?? 0),
  },
  calls: { id: 'calls', header: 'Lig.', label: 'Ligações', format: (r) => formatNumber(r.activities.calls) },
  emails: { id: 'emails', header: 'Emails', label: 'Emails', format: (r) => formatNumber(r.activities.emails) },
  messages: {
    id: 'messages',
    header: 'Msg.',
    label: 'Mensagens',
    format: (r) => formatNumber(r.activities.messages),
  },
  meetings: {
    id: 'meetings',
    header: 'Reun.',
    label: 'Reuniões',
    format: (r) => formatNumber(r.activities.meetings),
  },
  goal: { id: 'goal', header: 'Meta', label: 'Meta' },
};

export const isScorecardColumnId = (v: unknown): v is ScorecardColumnId =>
  typeof v === 'string' && (SCORECARD_COLUMN_IDS as readonly string[]).includes(v);
